// ===========================================================================
// Charge Capture (Módulo N) — Constantes.
//
// Fuente: docs/modulos/ChargeCapture/CONCEPCION_PROTOTIPO_CHARGE_CAPTURE.md
// v3.0, anclada a los endpoints reales de MCM.API.
//
// La mesa de trabajo donde billing convierte visitas atendidas en cargos
// posteados en AdvancedMD. **CDCI origina el slip; este módulo lo postea** —
// un solo punto de escritura hacia AMD.
// ===========================================================================

export const CHARGE_CAPTURE_BASE_PATH = "/charge-capture";

// ---------------------------------------------------------------------------
// Semántica de color por FUENTE (§13, regla dura): el usuario sabe sin leer de
// dónde viene lo que mira. Nunca FB en teal ni AMD en púrpura.
// ---------------------------------------------------------------------------
export const SOURCE_COLORS = {
  amd: { key: "amd", label: "AdvancedMD", color: "#0F6E6E", bg: "#E2F0EF" },
  fb: { key: "fb", label: "Florida Blue", color: "#6B4FA0", bg: "#EFEAF6" },
  mcm: { key: "mcm", label: "MCM edit", color: "#1B5FA8", bg: "#E4EEF8" },
};

// ---------------------------------------------------------------------------
// Estados del slip (§4.3). `IN_REVIEW` expira por inactividad para que un slip
// no quede bloqueado por una pestaña olvidada.
// ---------------------------------------------------------------------------
export const SLIP_STATUS_META = {
  PENDING: { key: "PENDING", label: "Pending", tone: "info", actor: "Biller" },
  IN_REVIEW: { key: "IN_REVIEW", label: "In review", tone: "warn", actor: "That biller" },
  WAITING_MD: { key: "WAITING_MD", label: "Waiting on physician", tone: "warn", actor: "Physician" },
  ANSWERED: { key: "ANSWERED", label: "Answered", tone: "info", actor: "Biller" },
  READY_TO_POST: { key: "READY_TO_POST", label: "Ready to post", tone: "ok", actor: "System" },
  POSTED: { key: "POSTED", label: "Posted", tone: "ok", actor: "—" },
  POST_ERROR: { key: "POST_ERROR", label: "Post error", tone: "crit", actor: "Biller / supervisor" },
  NOT_BILLABLE: { key: "NOT_BILLABLE", label: "Not billable", tone: "muted", actor: "—" },
};

/** Slips que siguen siendo trabajo del biller (los que van a la bandeja). */
export const OPEN_STATUSES = ["PENDING", "IN_REVIEW", "WAITING_MD", "ANSWERED", "POST_ERROR"];

/** Minutos de inactividad tras los que expira el bloqueo de un slip (§4.3). */
export const LOCK_TTL_MINUTES = 20;

// ---------------------------------------------------------------------------
// Severidad por antigüedad — mismos cortes que el detector (§4.1).
// ---------------------------------------------------------------------------
export const SEVERITY_META = {
  Critical: { key: "Critical", label: "Critical (>72h)", tone: "crit" },
  High: { key: "High", label: "High (48–72h)", tone: "warn" },
  Warning: { key: "Warning", label: "Warning (24–48h)", tone: "info" },
  Normal: { key: "Normal", label: "Normal (≤24h)", tone: "muted" },
};

/** Antigüedad como escala CONTINUA de color (§13), alineada a la severidad. */
export const ageColor = (hours) => {
  const f = Math.max(0, Math.min(1, hours / 96));
  const lerp = (a, b, t) => Math.round(a + (b - a) * t);
  const [from, to, t] =
    f < 0.5
      ? [[0x7c, 0x8b, 0x93], [0xb5, 0x76, 0x0a], f / 0.5]
      : [[0xb5, 0x76, 0x0a], [0xc0, 0x39, 0x2b], (f - 0.5) / 0.5];
  const c = from.map((v, i) => lerp(v, to[i], t));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
};

/** Antigüedad legible a partir de horas. */
export const fmtAge = (hours) => (hours < 48 ? `${hours}h` : `${Math.floor(hours / 24)}d`);

// ---------------------------------------------------------------------------
// Observación al médico (§8) — NUNCA se llama "chat" en la interfaz.
// ---------------------------------------------------------------------------
export const OBSERVATION_STATUS = {
  OPEN: { key: "OPEN", label: "Open", tone: "warn" },
  ANSWERED: { key: "ANSWERED", label: "Answered", tone: "info" },
  RESOLVED: { key: "RESOLVED", label: "Resolved", tone: "ok" },
  NO_RESPONSE: { key: "NO_RESPONSE", label: "No response — escalate", tone: "crit" },
};

/** SLA de respuesta antes de escalar, y tope de observaciones por médico (§8.6). */
export const OBSERVATION_SLA_DAYS = 5;
export const OBSERVATION_CAP_PER_MD = 5;

/**
 * Plantillas NO DIRIGIDAS (§8.2): presentan evidencia y preguntan abierto.
 * Las opciones SIEMPRE incluyen la salida negativa — una consulta sin
 * "no puedo determinarlo" es una consulta dirigida, y eso es riesgo de auditoría.
 */
export const OBSERVATION_TEMPLATES = [
  {
    key: "dx_specificity",
    label: "Diagnosis specificity",
    when: "There is clinical evidence of a more specific condition",
    prompt:
      "The findings of this visit are shown above. Is there a documented relationship you can specify for this patient's condition?",
    options: [
      "I will document the specific condition in the note",
      "The documentation is correct as written",
      "None of the above / I cannot determine it",
    ],
  },
  {
    key: "em_support",
    label: "Level of service support",
    when: "The E/M level is not supported by the note",
    prompt: "What level of complexity corresponds to the care provided at this visit?",
    options: [
      "I will clarify the complexity in the note",
      "The documented level is correct",
      "None of the above / I cannot determine it",
    ],
  },
  {
    key: "unsigned_note",
    label: "Unsigned note",
    when: "The note exists but is not signed",
    prompt: "The note for this visit is unsigned. Signing it in AdvancedMD makes the visit billable.",
    options: ["I will sign it in AdvancedMD", "This note should not be signed", "I cannot determine it"],
  },
  {
    key: "no_documentation",
    label: "Visit without documentation",
    when: "There was an appointment but no note on file",
    prompt: "This appointment shows as attended but no note is on file. Was billable care provided?",
    options: ["Care was provided — I will document it", "No billable care was provided", "I cannot determine it"],
  },
  {
    key: "proc_unsupported",
    label: "Procedure without support",
    when: "A charge line is not supported by the documentation",
    prompt: "Which services documented at this visit support the procedures performed?",
    options: [
      "I will document the supporting service",
      "The documentation already supports it",
      "I cannot determine it",
    ],
  },
  {
    key: "confirm_not_billable",
    label: "Confirm not billable",
    when: "Presumed administrative visit",
    prompt: "This visit appears administrative. Can you confirm whether billable care was provided?",
    options: ["Confirmed — not billable", "Billable care WAS provided", "I cannot determine it"],
  },
];

/** Desenlaces de la respuesta del médico (§8.4) — acción, no solo texto. */
export const MD_OUTCOMES = {
  WILL_FIX_NOTE: { key: "WILL_FIX_NOTE", label: "I will adjust the note" },
  DOC_CORRECT: { key: "DOC_CORRECT", label: "The documentation is correct" },
  NOT_BILLABLE: { key: "NOT_BILLABLE", label: "No billable care was provided" },
};

/** Tabs del módulo. La estación vive DENTRO de la bandeja (maestro-detalle). */
export const CC_TABS = {
  TRAY: "tray",
  RESULTS: "results",
};

/** Atajos de teclado (§14). */
export const KEYS = {
  next: "j",
  prev: "k",
  open: "Enter",
  diagSearch: "d",
  procSearch: "p",
  observation: "o",
  post: "Ctrl+Enter",
  back: "Escape",
};
